import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "../Navbar";
import "./Boost.css";
import {
  BOOST_MAX_VIEWS,
  formatDate,
  formatTime,
  getBoostAnalytics,
} from "../../services/boostApi";

// =====================================================
// BOOST ANALYTICS  —  /boost/:blogId/analytics
//
// Everything on this page is read from the campaign ledger on the server:
// total unique views, how many of the 50 are left, when the boost started
// and the date + time of every individual view.
//
// Readers are reported as a position, never as an identity — the owner is
// told how many different people read the blog and when, which is the same
// privacy the existing unique-view counter already gives every reader.
// =====================================================
function BoostAnalytics() {
  const { blogId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [blog, setBlog] = useState(null);
  const [boost, setBoost] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await getBoostAnalytics(blogId);
      setBlog(data.blog || null);
      setBoost(data.boost || null);
    } catch (err) {
      setError(
        err?.response?.data?.message || "Unable to load the analytics for this blog."
      );
    } finally {
      setLoading(false);
    }
  }, [blogId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="boost-page">
        <Navbar />
        <div className="boost-container">
          <div className="boost-loading">
            <div className="boost-spinner" />
            <p>Loading analytics…</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="boost-page">
        <Navbar />
        <div className="boost-container">
          <div className="boost-card boost-error-card">
            <h2>Analytics unavailable</h2>
            <p>{error}</p>
            <button type="button" className="boost-ghost-btn" onClick={() => navigate(-1)}>
              ← Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const maxViews = boost?.maxViews ?? BOOST_MAX_VIEWS;
  const uniqueViews = boost?.uniqueViews ?? 0;
  const remaining = boost?.remainingViews ?? maxViews;
  const percent = Math.min(100, (uniqueViews / maxViews) * 100);
  const views = boost?.views || [];

  return (
    <div className="boost-page">
      <Navbar />

      <div className="boost-container">
        <div className="boost-header">
          <div className="boost-badge">📊 Analytics</div>
          <h1 className="boost-title">Boost Analytics</h1>
          {blog?.title && <p className="boost-sub">“{blog.title}”</p>}
        </div>

        {!boost ? (
          <div className="boost-card boost-empty-card">
            <div className="boost-empty-icon">🚀</div>
            <p>This blog has not been boosted yet.</p>
            <button
              type="button"
              className="boost-pay-btn"
              onClick={() => navigate(`/boost/${blogId}`)}
            >
              Boost Your Blog
            </button>
          </div>
        ) : (
          <>
            {/* STATUS + PROGRESS */}
            <div className="boost-card">
              <div className="boost-progress-row">
                <span className={`boost-status ${boost.isActive ? "is-active" : "is-done"}`}>
                  {boost.isActive ? "🟢 Boost Active" : "✅ Boost Completed"}
                </span>
                <span className="boost-progress-count">
                  {uniqueViews}/{maxViews}
                </span>
              </div>
              <div className="boost-progress-track">
                <div className="boost-progress-fill" style={{ width: `${percent}%` }} />
              </div>
              {!boost.isActive && (
                <p className="boost-meta-line">
                  The boost stopped automatically at {maxViews}/{maxViews}. This blog is now
                  shown in the normal feed.
                </p>
              )}
            </div>

            {/* NUMBERS */}
            <div className="boost-stat-grid">
              <div className="boost-stat">
                <span className="boost-stat-value">{uniqueViews}</span>
                <span className="boost-stat-label">Total Unique Views</span>
              </div>
              <div className="boost-stat">
                <span className="boost-stat-value">{remaining}</span>
                <span className="boost-stat-label">Remaining Views</span>
              </div>
              <div className="boost-stat">
                <span className="boost-stat-value">
                  {uniqueViews}/{maxViews}
                </span>
                <span className="boost-stat-label">View Count</span>
              </div>
            </div>

            {/* CAMPAIGN DETAIL */}
            <div className="boost-card">
              <h3 className="boost-card-heading">Boost details</h3>
              <div className="boost-receipt-rows">
                <div className="boost-receipt-row">
                  <span>Boost Start Date</span>
                  <strong>{formatDate(boost.startedAt)}</strong>
                </div>
                <div className="boost-receipt-row">
                  <span>Boost Start Time</span>
                  <strong>{formatTime(boost.startedAt)}</strong>
                </div>
                <div className="boost-receipt-row">
                  <span>Boost Status</span>
                  <strong className={boost.isActive ? "boost-text-active" : "boost-text-done"}>
                    {boost.isActive ? "Active" : "Completed"}
                  </strong>
                </div>
                <div className="boost-receipt-row">
                  <span>Maximum Users</span>
                  <strong>{maxViews}</strong>
                </div>
                <div className="boost-receipt-row">
                  <span>Paid Amount</span>
                  <strong>₹{boost.amount}</strong>
                </div>
                {boost.completedAt && (
                  <div className="boost-receipt-row">
                    <span>Completed</span>
                    <strong>
                      {formatDate(boost.completedAt)} · {formatTime(boost.completedAt)}
                    </strong>
                  </div>
                )}
              </div>
            </div>

            {/* INDIVIDUAL VIEWS */}
            <div className="boost-card">
              <h3 className="boost-card-heading">Individual views</h3>
              {views.length === 0 ? (
                <p className="boost-meta-line">
                  No reader has opened this boosted blog yet. Views appear here with their date
                  and time.
                </p>
              ) : (
                <div className="boost-view-list">
                  {views.map((v) => (
                    <div className="boost-view-row" key={v.position}>
                      <span className="boost-view-index">#{v.position}</span>
                      <span className="boost-view-date">{formatDate(v.viewedAt)}</span>
                      <span className="boost-view-time">{formatTime(v.viewedAt)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        <div className="boost-actions">
          <button type="button" className="boost-secondary-btn" onClick={load}>
            ↻ Refresh
          </button>
          <button type="button" className="boost-ghost-btn" onClick={() => navigate("/pro")}>
            ← Back to Profile
          </button>
        </div>
      </div>
    </div>
  );
}

export default BoostAnalytics;
